import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useNavigate, useSearchParams } from "react-router"
import { useSupplierArticles } from "../../../api/suppliersApi"
import type { ArticleRow } from "../../../api/types/masterData"
import { CollapsibleSection } from "../../shared/CollapsibleSection"
import {
  EntitySubTable,
  type SubTableColumn,
} from "../../shared/EntitySubTable"
import { McPill } from "../../shared/McPill"

export function SupplierArticlesSection() {
  const [searchParams] = useSearchParams()
  const id = searchParams.get("id")
  const navigate = useNavigate()
  const { translate } = useTranslation()

  const { data, hasNextPage, fetchNextPage, isLoading } = useSupplierArticles(
    id,
    { limit: 20 },
  )

  const articles = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total

  if (!isLoading && articles.length === 0) return null

  function getSupplierArticleNumber(a: ArticleRow): string | null {
    const link = a.suppliers?.find((s) => s.id === id)
    return link?.supplierArticleNumber ?? null
  }

  const columns: SubTableColumn<ArticleRow>[] = [
    {
      key: "articleNumber",
      header: translate("MC_MD_PANEL_COL_ARTICLE"),
      cell: (a) => <span className="font-mono">{a.articleNumber}</span>,
      className: "w-40",
    },
    {
      key: "supplierArticleNumber",
      header: translate("MC_MD_PANEL_COL_SUPPLIER_ARTICLE"),
      cell: (a) => {
        const san = getSupplierArticleNumber(a)
        return san ? (
          <span className="font-mono">{san}</span>
        ) : (
          <span className="text-grey5">—</span>
        )
      },
      className: "w-40",
    },
    {
      key: "name",
      header: translate("MC_MD_PANEL_COL_DESCRIPTION"),
      cell: (a) => <span className="block max-w-xs truncate">{a.name}</span>,
    },
    {
      key: "materialGroup",
      header: translate("MC_MD_FIELD_MATERIAL_GROUP"),
      cell: (a) =>
        a.materialGroup ? (
          <McPill tone="muted">{a.materialGroup}</McPill>
        ) : (
          <span className="text-grey5">—</span>
        ),
      className: "w-28",
      hideBelow: "360px",
    },
    {
      key: "restricted",
      header: translate("MC_MD_PANEL_COL_RESTRICTED"),
      cell: (a) =>
        a.restrictedSubstancesCount > 0 ? (
          <McPill tone="attention">{a.restrictedSubstancesCount}</McPill>
        ) : (
          <span className="text-grey5">—</span>
        ),
      className: "w-24",
      hideBelow: "280px",
    },
  ]

  return (
    <CollapsibleSection
      label={translate("MC_MD_PANEL_ARTICLES_TITLE")}
      badge={<span className="text-xs text-grey5">{total}</span>}
      className="px-4 pb-4"
      hideContentBorder
    >
      <EntitySubTable
        title={translate("MC_MD_PANEL_ARTICLES_TITLE")}
        items={articles}
        columns={columns}
        keyOf={(a) => a.id}
        hasMore={!!hasNextPage}
        onFetchMore={fetchNextPage}
        isLoading={isLoading}
        total={total}
        onRowClick={(a) => navigate(`../articles?id=${a.id}&source=nav`)}
        hideHeader
        bordered={false}
        className="px-0 pb-0"
        footerClassName="px-4"
      />
    </CollapsibleSection>
  )
}
